import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { ACCESS_TOKEN, LOGIN_SETTINGS, REFRESH_TOKEN } from '~/app/constants.js';
import { actions as appActions } from '~/app/redux/modules/app';
import { actions as loginSetupActions } from '~/app/redux/loginSetup/duck';
import WebHelpers from '~/app/webHelpers.js';

/**
 * Persists exchanged handoff tokens and advances post-login routing.
 *
 * @param {Object} params
 * @param {Object} params.responseData Raw API payload from /authn/sso/handoff.
 * @param {Object} params.history React Router history instance.
 * @param {Function} params.dispatch Redux dispatch for stage/finalize actions.
 * @param {Function} params.setIsFinalizingLogin Local state setter from Handoff.jsx.
 * @param {{current: boolean}} params.sawFinalizeAuthCycleRef Ref tracking whether
 * finalize auth processing has started at least once.
 * @param {Object} [params.deps] Optional dependency overrides for focused unit tests.
 * @returns {'toc'|'irs'|'finalize'} Branch taken after token persistence.
 */
export const processSSOHandoffResponse = ({
  responseData,
  history,
  dispatch,
  setIsFinalizingLogin,
  sawFinalizeAuthCycleRef,
  deps = {},
}) => {
  const decodeJWT = deps.decodeJWT || jwtDecode;
  const cleanupSession = deps.cleanupSession || WebHelpers.cleanupSession;
  const cleanupLocalStorage = deps.cleanupLocalStorage || WebHelpers.cleanupLocalStorage;
  const dispatchToStore = deps.dispatchToStore || dispatch;
  const app = deps.appActions || appActions;
  const loginSetup = deps.loginSetupActions || loginSetupActions;

  const accessToken = responseData?.access_token;
  const refreshToken = responseData?.refresh_token;
  const irsMessage = String(responseData?.irsMessage ?? '');
  // Legacy API semantics: `termsAndConditions=true` means already accepted.
  // We store the inverse because login flow uses SHOW_TERMS_AND_CONDITIONS.
  const showTandC = !responseData?.termsAndConditions;

  if (!accessToken || !refreshToken) {
    throw new Error('Invalid SSO handoff response');
  }

  const tokenExp = decodeJWT(accessToken)?.exp;

  // Prevent stale local session context from mixing with incoming handoff session.
  cleanupSession();
  cleanupLocalStorage();

  sessionStorage.setItem(ACCESS_TOKEN, accessToken);
  localStorage.setItem(REFRESH_TOKEN, refreshToken);
  sessionStorage.setItem(LOGIN_SETTINGS.SHOW_IRS_MESSAGE, irsMessage);
  sessionStorage.setItem(LOGIN_SETTINGS.SHOW_TERMS_AND_CONDITIONS, showTandC ? 'true' : 'false');
  // Refresh timer must be updated immediately after token persistence so
  // auto-logout is aligned with the newly issued access token lifetime.
  dispatchToStore(app.updateRefreshTimer(tokenExp));

  // Remove handoff params from URL once tokens are persisted.
  history.replace({ pathname: '/sso-handoff', hash: '', search: '' });

  if (showTandC) {
    // Terms page takes precedence over IRS message when both might be flagged.
    dispatch(app.setAuthStageAwaitTermsAndConditions());
    history.push('/terms-and-conditions');
    return 'toc';
  }

  if (irsMessage === 'true') {
    dispatch(app.setAuthStageAwaitIRSMessage());
    history.push('/irs-message');
    return 'irs';
  }

  // Default branch: no interstitial page required.
  // Keep auth stage aligned with MFA/finalize pipeline used by the app.
  dispatch(app.setAuthStageAwaitSecondFactor());
  // Reset cycle marker before dispatching finalize so failure detection in
  // Handoff.jsx waits for a fresh authenticate->idle transition.
  sawFinalizeAuthCycleRef.current = false;
  setIsFinalizingLogin(true);
  dispatch(loginSetup.onFinalizeLogin());
  return 'finalize';
};

/**
 * Starts the one-time handoff code exchange and returns a cleanup function.
 *
 * @param {Object} params
 * @param {string} params.search URL query string (for example, ?code=abc123).
 * @param {Function} [params.createCancelSource] Injectable cancel source factory.
 * @param {Function} [params.isCancel] Injectable axios cancellation predicate.
 * @param {Function} params.exchangeSSOHandoffCode Exchange API function.
 * @param {Function} params.onProcessResponse Called with successful API response.
 * @param {Function} params.onExchangeFailure Called for non-cancel exchange failures.
 * @param {Function} params.onMissingCode Called when `code` query param is absent.
 * @returns {Function} Cleanup that cancels any in-flight exchange request.
 */
export const startSSOHandoffExchange = ({
  search,
  createCancelSource,
  isCancel,
  exchangeSSOHandoffCode,
  onProcessResponse,
  onExchangeFailure,
  onMissingCode,
}) => {
  const searchParams = new URLSearchParams(search);
  const handoffCode = searchParams.get('code');

  if (!handoffCode) {
    onMissingCode();
    return () => {};
  }

  const sourceFactory = createCancelSource || (() => axios.CancelToken.source());
  const isCancelError = isCancel || axios.isCancel;
  const source = sourceFactory();
  // Guard async continuations to avoid state/navigation writes after unmount.
  let isActive = true;

  exchangeSSOHandoffCode(handoffCode, { cancelToken: source.token })
    .then(res => {
      if (!isActive) {
        return;
      }

      onProcessResponse(res);
    })
    .catch(error => {
      if (!isActive || isCancelError(error)) {
        return;
      }

      onExchangeFailure(error);
    });

  return () => {
    isActive = false;
    // Keep message stable for test assertions and future diagnostics.
    source.cancel('SSO handoff request was canceled');
  };
};
